import { zodResolver } from "@hookform/resolvers/zod";
import { Group } from "@mantine/core";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";

import { spacing } from "#app/theme/v2/spacing";
import { InputNumber } from "#components/atoms/InputNumber/InputNumber";
import {
  BulkActionBar,
  BulkActionBarActionDisabledProps,
} from "#components/molecules/BulkActionBar/BulkActionBar";
import { useSurfaceTreatmentOptions } from "#modules/quoteRequestLinesV2/hooks/useSurfaceTreatmentSelect";
import { MaterialSelect } from "#modules/quoteRequestLinesV2/view/molecules/MaterialSelect";
import { PartFormatSelect } from "#modules/quoteRequestLinesV2/view/molecules/PartFormatSelect";
import { SurfaceTreatmentSelect } from "#modules/quoteRequestLinesV2/view/molecules/SurfaceTreatmentSelect";
import { Icon } from "#shared/icons/Icon";
import { MaterialGradeSchema, PartTypeSchema } from "shared";

const FIELD_WIDTH = 160;

type Props = {
  selectedCount: number;
  hasAnalyzingSelectedLine: boolean;
  onClearSelection: () => void;
  selectedIds: string[];
};

const schema = z
  .object({
    type: PartTypeSchema.nullable(),
    quantity: z.number().min(1).nullable(),
    material: z
      .object({
        materialName: z.string(),
        materialGrade: MaterialGradeSchema.nullable(),
      })
      .nullable(),
    treatments: z.array(z.string()).nullable(),
  })
  .superRefine((values, ctx) => {
    if (Object.values(values).every((value) => value === null)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "At least one value must be provided",
      });
    }
  });

type FormValues = z.infer<typeof schema>;

export const QuoteRequestLinesBulkActionBar = ({
  selectedCount,
  hasAnalyzingSelectedLine,
  onClearSelection,
  selectedIds,
}: Props) => {
  const { t } = useTranslation();

  const { data: surfaceTreatmentOptions } = useSurfaceTreatmentOptions();

  const { control, handleSubmit, reset, formState } = useForm<FormValues>({
    defaultValues: {
      type: null,
      quantity: null,
      material: null,
      treatments: null,
    },
    resolver: zodResolver(schema),
    mode: "onChange",
  });

  const onSave = handleSubmit((values) => {
    window.console.log({ selectedIds, values });
  });

  const getSaveButtonDisabledProps = (): BulkActionBarActionDisabledProps => {
    if (hasAnalyzingSelectedLine) {
      return {
        disabled: true as const,
        disabledTooltip: t("quoteRequestV2.bulkActions.analyzingTooltip"),
      };
    }
    if (!formState.isValid) {
      return {
        disabled: true as const,
        disabledTooltip: t("quoteRequestV2.bulkActions.atLeastOneValueRequired"),
      };
    }
    return {
      disabled: false as const,
      disabledTooltip: undefined,
    };
  };

  const saveButtonDisabledProps = getSaveButtonDisabledProps();

  return (
    <BulkActionBar
      label={t("quoteRequestV2.bulkActions.selectedCount", { count: selectedCount })}
      clearAriaLabel={t("quoteRequestV2.bulkActions.clearSelection")}
      onClear={onClearSelection}
      actions={[
        {
          key: "delete",
          icon: <Icon name="trash" size={16} />,
          ariaLabel: t("quoteRequestV2.bulkActions.delete"),
          disabled: true,
          disabledTooltip: "Not implemented",
          onClick: () => {},
        },
        {
          key: "cancel",
          icon: null,
          label: t("quoteRequestV2.bulkActions.cancel"),
          onClick: () => {
            reset();
            onClearSelection();
          },
        },
        {
          key: "save",
          icon: null,
          label: t("quoteRequestV2.bulkActions.save"),
          variant: "primary",
          ...saveButtonDisabledProps,
          onClick: onSave,
        },
      ]}
    >
      <Group wrap="nowrap" align="center" gap={spacing[2]}>
        <div style={{ width: FIELD_WIDTH }}>
          <Controller
            name="type"
            control={control}
            render={({ field }) => (
              <PartFormatSelect value={field.value} onConfirm={field.onChange} />
            )}
          />
        </div>
        <div style={{ width: FIELD_WIDTH }}>
          <Controller
            name="quantity"
            control={control}
            render={({ field }) => (
              <InputNumber
                value={field.value}
                onChange={field.onChange}
                onClear={() => field.onChange(null)}
                min={1}
                placeholder={t("quoteRequestV2.bulkActions.quantity")}
                aria-label={t("quoteRequestV2.bulkActions.quantity")}
              />
            )}
          />
        </div>
        <div style={{ width: FIELD_WIDTH }}>
          <Controller
            name="material"
            control={control}
            render={({ field }) => (
              <MaterialSelect
                value={field.value}
                onChange={(materialName, materialGrade) =>
                  field.onChange({ materialName, materialGrade })
                }
                placeholder={t("quoteRequestV2.bulkActions.material")}
                aria-label={t("quoteRequestV2.bulkActions.material")}
              />
            )}
          />
        </div>
        <div style={{ width: FIELD_WIDTH }}>
          <Controller
            name="treatments"
            control={control}
            render={({ field }) => (
              <SurfaceTreatmentSelect
                data={surfaceTreatmentOptions}
                value={field.value ?? []}
                onChange={(values) => field.onChange(values.length === 0 ? null : values)}
                placeholder={t("quoteRequestV2.bulkActions.treatments")}
              />
            )}
          />
        </div>
      </Group>
    </BulkActionBar>
  );
};
